import React, { type PropsWithChildren } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Pressable, StyleSheet, View } from 'react-native';

import { theme } from '@garnish/constants';
import { useResponsive } from '@garnish/core';

import { BUNDLE_DIALOG_HEIGHT, BUNDLE_DIALOG_WIDTH } from '../../Bundle.constants';

// ─────────────────────────────────────────────────────────────────────────────

export const BundleContainer = (props: Props) => {
  const { children, onDismiss } = props;

  const { formatMessage } = useIntl();
  const { currentBreakpoint } = useResponsive();

  // ─── Derived ─────────────────────────────────────────────────────────

  const isDialog = !currentBreakpoint.isXS;

  // ─── Styles ──────────────────────────────────────────────────────────

  const containerStyles = [styles.container, isDialog ? styles.containerDialog : undefined];
  const panelStyles = [styles.panel, isDialog ? styles.panelDialog : styles.panelFullScreen];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={containerStyles}>
      {isDialog ? (
        <Pressable
          accessibilityLabel={formatMessage(messages.dismiss)}
          accessibilityRole="button"
          style={styles.backdrop}
          onPress={onDismiss}
        />
      ) : null}

      {/* NOTE: Remounts on a layout change, since the panel keeps the dialog's height otherwise. */}
      <View key={isDialog ? 'dialog' : 'full-screen'} style={panelStyles}>
        {children}
      </View>
    </View>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const DIALOG_SHADOW = '0px 12px 48px 8px rgba(14, 21, 14, 0.12)';

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  containerDialog: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing['10'],
  },
  backdrop: {
    position: 'absolute',
    inset: 0,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.DARK,
  },
  panel: {
    overflow: 'hidden',
    backgroundColor: theme.colors.CREAM,
  },
  panelFullScreen: {
    flex: 1,
  },
  panelDialog: {
    flexShrink: 1,
    width: BUNDLE_DIALOG_WIDTH,
    maxWidth: '100%',
    height: BUNDLE_DIALOG_HEIGHT,
    borderRadius: theme.radius.xlarge,
    boxShadow: DIALOG_SHADOW,
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  dismiss: {
    defaultMessage: 'Dismiss',
    description: 'Bundle | Backdrop | Dismiss accessibility label',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = PropsWithChildren<{
  onDismiss: () => void;
}>;
